import React from "react";
import {Button, Text, CheckBox, ColorPickerBox, Column, Row, Spinner, TextInput} from "react-vcomponents";
import {InfoButton, TextPlus} from "web-vcore";
import {ObjPath} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {SpinnerForPercent} from "../../../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {TexturePicker} from "../../../../../../Utils/ReactComponents/TexturePicker.js";
import {Vec2} from "../../../../../../Utils/ThreeJS/VectorStructs.js";
import {PathEditorUI_SharedProps} from "../PathEditorUI.js";
import {ResolveObjPosInWorld} from "../../../../../../Server/DB/mapObjects/$mapObject.js";
import {GetMapObjects} from "../../../../../../Server/DB/mapObjects.js";
import {observer_mgl} from "mobx-graphlink";

export const PathEditorTab_General = observer_mgl((props: {} & PathEditorUI_SharedProps)=>{
	const {map, obj, path, index, Change} = props;
	const objPos = ResolveObjPosInWorld(obj);
	const allMapObjects = GetMapObjects(map.id);
	const pathIdDuplicate = !!path.path_id && (allMapObjects ?? []).some(obj2=>{
		return (obj2.paths ?? []).some(path2=>{
			if (obj2.id === obj.id && obj2.paths!.indexOf(path2) === index) return false; // skip self
			return path2.path_id === path.path_id;
		});
	});

	return <>
		<Row mt={5}>
			<TextPlus info={`
				The path's stable identifier for RNG seeding, falling back to a deterministic string when path_id is unset.
				Used by the path mesh generator, the isoline engine, and the resolved-values UI so all three agree.
				Note: The seed for isolines also is affected by the terrain's world-seed, so its shape changes when toggling "Terrain isoline".
			`.AsMultiline(0)}>Path ID:</TextPlus>
			{pathIdDuplicate && <Button mdIcon="alert" title="Path ID is not unique! (so most 'random' properties are shared/mirrored)"
				ml={4} p="0 4px" style={{color: "orange"}}/>}
			<TextInput ml={5} style={{flex: 1}} value={path.path_id ?? ""} onChange={val=>Change(a=>a.path_id = val === "" ? undefined : val)}/>
			<Button mdIcon="dice-multiple" ml={5} title="Generate a new random path ID" onClick={()=>Change(a=>a.path_id = ObjPath.NewPathID())}/>
		</Row>

		<Row mt={10} style={{fontWeight: "bold"}}>Rendering</Row>
		<Row mt={5}>
			<CheckBox text="Render" value={!path.renderDisabled} onChange={val=>{
				Change(a=>a.renderDisabled = val ? undefined : true);
			}}/>
		</Row>
		<Row mt={5}>
			<Text>Width (m):</Text>
			<Spinner ml={5} min={0} step={0.5} placeholder="1" value={path.width} onChange={val=>Change(a=>a.width = val)}/>
			{/*<Text ml={5}>Join type:</Text>
			<Select ml={5} options={GetEntries(JoinType, "ui")} value={path.joinType} onChange={val=>Change(a=>a.joinType = val)}/>*/}
			<Text ml={5}>Z offset:</Text>
			<Spinner ml={5} min={Number.MIN_SAFE_INTEGER} step="any" placeholder="0" value={path.zOffset} onChange={val=>Change(a=>a.zOffset = val)}/>
			<Text ml={5}>(world: {objPos.z + (path.zOffset ?? 0)})</Text>
		</Row>
		<Row mt={5}>
			<TextPlus info={`
				Maximum turn angle (degrees) allowed per outer-arc segment at path joints. Lower values
				produce smoother arcs (more, shorter segments); higher values produce cheaper geometry.
				At very high values (eg. 9000) every joint degrades to a 1-segment bevel — sharp miter
				on the inner edge, single straight chord on the outer. Default: 15. null = legacy 8
				segments (the pre-2026-08-02 hard-coded value).
			`.AsMultiline(0)}>Max turn per arc-segment:</TextPlus>
			<Spinner ml={5} value={path.maxTurnPerArcSegment} onChange={val=>Change(a=>a.maxTurnPerArcSegment = val)}/>
			<Text ml={5}>degrees</Text>
		</Row>
		<Row mt={5}>
			<Text>Color:</Text>
			<ColorPickerBox ml={5} color={(path.color ?? [255,255,255]) as [number,number,number]} onChange={val=>Change(a=>a.color = val)}/>
			<InfoButton ml={5} text={`
				If set, and there's no texture, this color will be used as the path's color.
				If set, and the path also has a texture, this color will be used to colorize it. (with the alpha controlling the colorization intensity)
				Note: When paths are assigned a color, they end up showing a bit darker in-map than that color would normally appear, eg. on html elements. (eg. path's [43,43,43] is equivalent to css' [50,50,50])
				Key colors (in path/in-map color-space): Map background is [43,43,43].
			`.AsMultiline(0)}/>
			<Text ml={5}>Opacity:</Text>
			<SpinnerForPercent ml={5} style={{width: 50}} value={path.opacity ?? 1} onChange={val=>Change(a=>a.opacity = val)} title="Opacity of the mesh. (for colorization intensity, instead set color's alpha)"/>
		</Row>
		<Row mt={5}>
			<Text>Texture:</Text>
			<TexturePicker ml={5} value={path.texture_id} onChange={val=>Change(a=>a.texture_id = val)}/>
			<Column ml={5} style={{flex: 1}}>
				<Row style={{flex: 1}}>
					<Text>Size, X (m):</Text>
					<Spinner ml={5} min={0} step={0.1} style={{width: 50}} value={path.texture_size?.x ?? 0} onChange={val=>Change(a=>a.texture_size = new Vec2(val, a.texture_size?.y ?? 0))}/>
					<Text ml={5}>Y (m):</Text>
					<Spinner ml={5} min={0} step={0.1} style={{width: 50}} value={path.texture_size?.y ?? 0} onChange={val=>Change(a=>a.texture_size = new Vec2(a.texture_size?.x ?? 0, val))}/>
				</Row>
				<Row style={{flex: 1}}>
					{/*<Text>Rotation:</Text>
					<Spinner ml={5} step="any" style={{width: 50}} value={path.texture_rotation ?? 0} onChange={val=>Change(a=>a.texture_rotation = val)}/>
					<Button ml={5} p="2px 5px" text="-90" onClick={()=>changeRot(-90)}/>
					<Button ml={5} p="2px 5px" text="+90" onClick={()=>changeRot(90)}/>
					<Button ml={5} p="2px 5px" text="Fix" onClick={()=>changeRot(0)}/>*/}
				</Row>
			</Column>
		</Row>
		{/*<Row mt={5}>
			<Text>UV type:</Text>
			<Select ml={5} options={GetEntries(UVType, "ui")} value={path.uvType} onChange={val=>Change(a=>a.uvType = val)}/>
		</Row>*/}
	</>;
});